import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { RequirementsForm } from "./RequirementsForm";
import { BookingWindow } from "@/types/campaign";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function RequirementsSubmissionPage() {
  const [window, setWindow] = useState<BookingWindow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchWindow() {
      try {
        const data = await apiClient.getCurrentBookingWindow();
        if (data) {
          setWindow(data);
        } else {
          setError("No active booking window found.");
        }
      } catch (err) {
        setError("Failed to load booking window details.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchWindow();
  }, []);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !window) {
    return (
      <div className="flex h-screen flex-col items-center justify-center space-y-4">
        <h1 className="text-2xl font-bold">Booking Window Unavailable</h1>
        <p className="text-muted-foreground">
          {error || "There are no booking windows currently open for submission."}
        </p>
        <Button onClick={() => navigate("/brand-dashboard")}>
          Return to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-neutral-900">
            {window.name}
          </h1>
          <p className="mt-2 text-lg text-neutral-600">
            Submit your campaign requirements for the current booking window.
            Submissions close on {new Date(window.submission_end_date).toLocaleDateString()}.
          </p>
        </div>

        <RequirementsForm windowId={window.id} />
      </div>
    </div>
  );
}
